import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { Environment, getCurrentApiEnv } from '../config/Environment';
import { SIGNALR } from '../constants/signalr';
import useAuth0Credentials from '../hooks/useAuth0Credentials';
import { BusinessUnitService } from '../service';
import { verifyIsAdm } from '../utils/functions';

type SignalRContextValue = {
  connection: HubConnection | null;
  connectionError: HubConnection | null;
  loadingConnection: boolean;
  loadingConnectionError: boolean;
};

const SignalRContext = createContext<SignalRContextValue | undefined>(
  undefined
);

type SignalRProviderProps = {
  children: ReactNode;
};

const setNullConnection = (prev: HubConnection | null) => {
  if (prev) {
    prev.stop();
  }
  return null;
};

export const SignalRProvider = ({ children }: SignalRProviderProps) => {
  const [connection, setConnection] = useState<null | HubConnection>(null);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [connectionError, setConnectionError] = useState<null | HubConnection>(
    null
  );
  const [loadingConnectionError, setLoadingConnectionError] = useState(true);
  const isAdm = verifyIsAdm();
  const { userId } = useAuth0Credentials();
  const { funcSignalR } = Environment.baseUrl[getCurrentApiEnv()];
  const businessUnitService = new BusinessUnitService();

  const buildConnection = async (
    endpoint: string
  ): Promise<HubConnection | null> => {
    let businessId;

    if (isAdm) {
      businessId = userId;
    } else {
      businessId = await businessUnitService.getBusinessIdByOperator(userId);
    }

    if (!businessId) {
      return null;
    }

    const url = `${funcSignalR.url}${endpoint}?code=${
      funcSignalR.code
    }&userId=${businessId.toLowerCase()}`;

    return new HubConnectionBuilder()
      .withUrl(`${url}`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();
  };

  const signalROrderRead = async () => {
    if (!userId) {
      return;
    }

    const newConnection = await buildConnection(
      SIGNALR.OrderReadbyBusinessunit.EndPoint
    );

    if (!newConnection) {
      return;
    }

    setConnection((prev) => {
      if (prev && prev !== newConnection) {
        void prev.stop();
      }
      return newConnection;
    });
  };

  const starSignalROrderReadError = async () => {
    setLoadingConnectionError(true);
    if (connectionError) {
      await connectionError
        .start()
        .then()
        .catch((e: any) =>
          console.info(
            'SignalR - OrderReadbyBusinessunit (Connection failed): ',
            e
          )
        );
      setLoadingConnectionError(false);
    }
  };

  const starSignalROrderRead = async () => {
    setLoadingConnection(true);
    if (connection) {
      await connection
        .start()
        .then()
        .catch((e: any) =>
          console.info(
            'SignalR - OrderReadbyBusinessunit (Connection failed): ',
            e
          )
        );
      setLoadingConnection(false);
    }
  };

  const signalROrderReadError = async () => {
    if (!userId) {
      return;
    }

    const newConnection = await buildConnection(
      SIGNALR.OrderReadbyBusinessunitError.EndPoint
    );

    if (newConnection) {
      setConnectionError(newConnection);
    }
  };

  useEffect(() => {
    starSignalROrderReadError();
    return () => {
      if (connectionError) {
        connectionError.stop();
      }
    };
  }, [connectionError]);

  useEffect(() => {
    return () => {
      if (connectionError) {
        setConnectionError(null);
      }
    };
  }, []);

  useEffect(() => {
    starSignalROrderRead();
    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [connection]);

  useEffect(() => {
    if (!userId) {
      setConnection(setNullConnection);
      setConnectionError(setNullConnection);
      return;
    }

    void signalROrderRead();
    void signalROrderReadError();

    return () => {
      setConnection(setNullConnection);
      setConnectionError(setNullConnection);
    };
  }, [userId]);

  const value = useMemo(
    () => ({
      connection,
      connectionError,
      loadingConnection,
      loadingConnectionError,
    }),
    [connection, connectionError, loadingConnection, loadingConnectionError]
  );

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);

  if (!context) {
    throw new Error('useSignalR must be used within an SignalRProvider');
  }

  return context;
};

